import React from 'react'
import { Tabs, Badge } from 'antd'
import { UserDetails } from './UserDetails'
import { Prescription } from './Prescription'
import { CompleteConsultationForm } from './CompleteConsultationForm'
import { ChartingSection } from './ChartingSection'
import {
    WarningContainer,
    WarningText,
    ReviewContainer,
    UserDetailsSection,
    SectionDivider,
    PrescriptionDivider,
    TabsContainer,
    AuthSection,
    DEADisclaimer,
    PinInput,
    TFASection,
    TFAControlsRow,
    TFACodeInput,
    DuoButton,
} from './ReviewPrescriptions.styles'

const Warning = ({ children }) => (
    <WarningContainer>
        <WarningText>⚠️ {children}</WarningText>
    </WarningContainer>
)

export const ReviewPrescriptions = ({ 
    form,
    hasControlledSubstance, 
    hasIncompletePharmacyNotes,
    prescriptions = [],
    pharmacyNotesVariables = {},
    pharmacyNotesSubmitAttempted = false,
    onVariableChange,
    onReadyToSign,
    onSetProviderPin,
    onSetOTPCode,
    onUseTFA,
    providerPin,
    useTFA,
    tfaCode,
    provider,
    patient,
    chartingRequirementIsMet,
    onChartingNoteChange,
    patientMessage,
    onPatientMessageChange,
    onClearPatientMessage,
    includePatientMessage,
    onIncludePatientMessageChange,
    cdsOptions = [],
}) => {
    const handleSetProviderPin = ({ target }) => {
        if (onSetProviderPin) {
            onSetProviderPin(target.value)
        }
    }

    const handleSetOTPCode = ({ target }) => {
        if (onSetOTPCode) {
            onSetOTPCode(target.value)
        }
    }

    const handleUseTFA = () => {
        if (onUseTFA) {
            onUseTFA(!useTFA)
        }
    } 

    return (
        <ReviewContainer>
            <UserDetailsSection>
                { patient && <UserDetails type='patient' vertical name={`${patient?.firstName} ${patient.lastName}`} user={patient} style={{ minWidth: '33.33%' }}/> }
                <SectionDivider />
                { provider && <UserDetails type='provider' name={`${provider?.firstName} ${provider?.lastName}`} user={provider} style={{ minWidth: '33.33%' }}/> }
                <SectionDivider />
            </UserDetailsSection>
            { prescriptions.map((prescription, i) => {
                return <div key={prescription._id}>
                    <Prescription
                        prescription={prescription}
                        i={i}
                        onReadyToSign={onReadyToSign}
                        variableValues={pharmacyNotesVariables[prescription._id]}
                        submitAttempted={pharmacyNotesSubmitAttempted}
                        onVariableChange={onVariableChange}
                        cdsOptions={cdsOptions}
                    />
                    { i !== prescriptions.length - 1 && <PrescriptionDivider /> }
                </div>
            })}
            <PrescriptionDivider />
            <TabsContainer>
                <Tabs
                    defaultActiveKey="patient-message"
                    items={[{
                        key: "patient-message",
                        label: "Patient Message",

                        children: <><CompleteConsultationForm 
                                form={form} 
                                prescriptions={prescriptions} 
                                patientMessage={patientMessage} 
                                onPatientMessageChange={onPatientMessageChange}
                                onClearPatientMessage={onClearPatientMessage}
                                includePatientMessage={includePatientMessage}
                                onIncludePatientMessageChange={onIncludePatientMessageChange}
                            /></>
                    }, {
                        key: "charting",

                        label: <Badge count={!chartingRequirementIsMet ? '!' : 0} offset={[6, 0]}>
                            Charting
                        </Badge>,

                        forceRender: true,

                        children: <><ChartingSection
                                onChartingNoteChange={onChartingNoteChange}
                            /></>
                    }]} />
            </TabsContainer>
            <PrescriptionDivider />
            {
                provider?.pinSet && (
                    <>
                        <AuthSection>
                            { hasControlledSubstance && (
                                <DEADisclaimer>
                                    By completing the two-factor authentication protocol at this time, you are legally signing the 
                                    prescription(s) and authorizing the transmission of the above information to the pharmacy for 
                                    dispensing. The two-factor authentication protocol may only be completed by the practitioner whose 
                                    name and DEA registration number appear above.
                                </DEADisclaimer>
                            )}
                            <div>
                                PIN:
                                <PinInput
                                    maxLength={6}
                                    type="password"
                                    size="small"
                                    label="PIN"
                                    value={providerPin}
                                    onChange={handleSetProviderPin}
                                />
                            </div>
                            { hasControlledSubstance && (
                                <TFASection>
                                    <p>Please input your two-factor authentication security code from your DUO mobile application OR send a push notification to DUO</p>
                                    <TFAControlsRow>
                                        { !useTFA && (
                                            <span>
                                                TFA Code:
                                                <TFACodeInput
                                                    maxLength={6}
                                                    size="small"
                                                    label="TFA"
                                                    value={tfaCode}
                                                    onChange={handleSetOTPCode}
                                                />
                                                or
                                            </span>
                                        )}
                                        <DuoButton
                                            type={useTFA ? 'primary' : undefined}
                                            size="small"
                                            onClick={handleUseTFA}
                                        >
                                            {`Request${useTFA ? 'ing' : ''} DUO push`}
                                        </DuoButton>
                                    </TFAControlsRow>
                                </TFASection>
                            )}
                        </AuthSection>
                        <PrescriptionDivider />
                    </>
                )
            }
            { 
                hasIncompletePharmacyNotes && (
                    <Warning>
                        Please fill in all required pharmacy notes values above before completing the consultation.
                    </Warning>
                )
            }
        </ReviewContainer>
    );
}
